import { gql, useMutation, useQuery } from "@apollo/client";
import React, { useState } from "react";
import { useRouteMatch } from "react-router";
import { Table } from "src/components/layout/tables/Table";
import { Loading } from "src/components/Loading";
import { Toast } from "src/hooks/toast";
import { BreadCrumb, BreadCrumbProp } from "../../components/light/Breadcrumb";
import { Content } from "../../components/light/Content";
import { SectionBlock } from "../../components/light/SectionBlock";

const QUERY = gql`
  query shipmentTrackingCode($tracking_code: String!) {
    shipmentTrackingCode(tracking_code: $tracking_code) {
      id
      trackingCode
      reference
      createDate
      success
      reason
    }
  }
`;

const QUERY_P = gql`
  query sellerPackage($packageId: Int!) {
    sellerPackage(packageId: $packageId) {
      id
      supplier {
        id
        name
      }
      status
      internationalReceivedDate
      createdDate
      localReceivedDate
      international {
        id
        name
      }
      local {
        id
        name
      }
      nodes {
        id
        nodeId
      }
    }
  }
`;

const MUTATION = gql`
  mutation attachShipmentTrackingCode($trackingCode: String!, $reference: String!) {
    attachShipmentTrackingCode(trackingCode: $trackingCode, reference: $reference)
  }
`;

function RenderPackage({ packageId }: { packageId: number }) {
  const { data, loading } = useQuery(QUERY_P, {
    variables: {
      packageId,
    },
  });

  if (loading) {
    return <Loading />;
  }

  if (!data) {
    return (
      <td>
        <small>Package not found</small>
      </td>
    );
  }

  return (
    <td>
      {data.sellerPackage.nodes.map((n: any) => {
        return (
          <div className="bg bg-warning m-1 text-center">
            <small>{n.nodeId}</small>
          </div>
        );
      })}
    </td>
  );
}

function TrackingBarcodeForm({ tracking_code }: { tracking_code: string }) {
  let refInput: HTMLInputElement | null;
  const { data, loading } = useQuery(QUERY, {
    variables: {
      tracking_code,
    },
    fetchPolicy: "no-cache",
  });
  const [attachShipmentTrackingCode] = useMutation(MUTATION, {
    refetchQueries: ["merchantShipmentList", "shipmentTrackingCode"],
    onError: (err) => {
      Toast.onShow(err.message, "danger", "right");
    },
  });

  const handleEnter = () => {
    attachShipmentTrackingCode({
      variables: {
        trackingCode: tracking_code,
        reference: refInput!.value.trim(),
      },
    });

    refInput!.value = "";
  };

  return (
    <SectionBlock title="" transparent={true}>
      <div className="row">
        <div className="col-md-6">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Enter barcode here"
              ref={(ref) => (refInput = ref)}
              onKeyDown={(e) => e.keyCode === 13 && handleEnter()}
              autoFocus
            />
            <div className="input-group-prepend">
              <button
                className="input-group-text btn btn-primary btn-sm"
                onClick={handleEnter}
                style={{ borderRadius: "0 4px 4px 0", borderLeftWidth: 0 }}
              >
                Enter
              </button>
            </div>
          </div>
          <br />
          {!loading && (
            <Table border seperator stripe grid>
              <thead>
                <tr>
                  <th className="text-center">Status</th>
                  <th>Barcode</th>
                  <th className="text-center">Scanned Date</th>
                  <th>Reason</th>
                  <th>Package</th>
                </tr>
              </thead>
              <tbody>
                {data !== undefined &&
                  data.shipmentTrackingCode.map((x: any) => {
                    return (
                      <tr key={x.id}>
                        <td style={{ width: 50 }} className="bolder nowrap text-center">
                          {x.success ? (
                            <i className="text-success os-icon os-icon-ui-21"></i>
                          ) : (
                            <i className="text-danger os-icon os-icon-alert-circle"></i>
                          )}
                        </td>
                        <td className="bolder nowrap">{x.reference}</td>
                        <td className="text-center">{x.createDate}</td>
                        <td colSpan={(x.reference as string).substring(0, 2) === "PK" ? 1 : 2}>
                          <small>{!x.success && x.reason}</small>
                        </td>
                        {(x.reference as string).substring(0, 2) === "PK" && (
                          <RenderPackage packageId={Number((x.reference as string).substring(2))} />
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </SectionBlock>
  );
}

export function TrackingCodeScreen() {
  const match: any = useRouteMatch();
  const [tracking_code] = useState(match.params.id ? match.params.id : "");

  const BreadCrumbItems: BreadCrumbProp[] = [
    { text: "applications", to: "#" },
    { text: "Tracking Code", to: "/" },
    {
      text: !match.params.id ? "Code" : "Barcode",
      to: match.params.id ? "/tracking/" + match.params.id : "/tracking/",
    },
  ];

  return (
    <Content breadcrumb={<BreadCrumb items={BreadCrumbItems} />}>
      <div className="row">
        <div className="col-md-12">
          <TrackingBarcodeForm tracking_code={tracking_code} />
        </div>
      </div>
    </Content>
  );
}
